import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import './AnimatedBackground.scss';

interface AnimatedBackgroundProps {
  children: React.ReactNode;
  // Optional prop to explicitly disable the animation for specific components
  disableForComponents?: string[];
}

export const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  children,
  disableForComponents = []
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const location = useLocation();

  // Check if current path is one where we should disable the animation
  const shouldDisableAnimation = () => {
    const currentPath = location.pathname;

    // Disable for InteractiveMapPlanner page
    if (currentPath.includes('/map')) {
      return true;
    }

    // Also disable for any explicitly specified components
    return disableForComponents.length > 0;
  };

  const isAnimationDisabled = shouldDisableAnimation();

  useEffect(() => {
    // Don't run animation if disabled
    if (isAnimationDisabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let animationFrameId: number;
    const gradientColors = isDarkMode
      ? [
        'rgba(255, 175, 189, 0.15)',
        'rgba(255, 195, 160, 0.15)',
        'rgba(145, 234, 228, 0.15)',
        'rgba(134, 168, 231, 0.15)',
        'rgba(155, 225, 93, 0.15)',
        'rgba(0, 227, 174, 0.15)',
        'rgba(211, 148, 255, 0.15)'
      ]
      : [
        'rgba(255, 160, 180, 0.8)',
        'rgba(255, 180, 150, 0.8)',
        'rgba(255, 210, 160, 0.8)',
        'rgba(255, 230, 170, 0.8)',
        'rgba(210, 190, 255, 0.8)',
        'rgba(180, 210, 255, 0.8)',
        'rgba(160, 230, 220, 0.8)',
        'rgba(255, 190, 210, 0.8)'
      ];

    const circles: {
      x: number;
      y: number;
      radius: number;
      color: string;
      vx: number;
      vy: number;
      pulseSpeed: number;
      pulseDirection: number;
      scale: number;
    }[] = [];

    // Create more circles for a richer background
    const circleCount = isDarkMode ? 8 : 10;

    for (let i = 0; i < circleCount; i++) {
      circles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 250 + 150,
        color: gradientColors[i % gradientColors.length],
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        pulseSpeed: Math.random() * 0.003 + 0.001,
        pulseDirection: 1,
        scale: 1.0
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      circles.forEach(circle => {
        // Move circles
        circle.x += circle.vx;
        circle.y += circle.vy;

        // Bounce off walls
        if (circle.x - circle.radius < 0 || circle.x + circle.radius > canvas.width) {
          circle.vx *= -1;
        }
        if (circle.y - circle.radius < 0 || circle.y + circle.radius > canvas.height) {
          circle.vy *= -1;
        }

        // Pulse effect - circles grow and shrink (with smaller range)
        circle.scale += circle.pulseSpeed * circle.pulseDirection;
        if (circle.scale > 1.05) {
          circle.pulseDirection = -1;
        } else if (circle.scale < 0.95) {
          circle.pulseDirection = 1;
        }

        const currentRadius = circle.radius * circle.scale;

        // Create gradient
        const gradient = ctx.createRadialGradient(
          circle.x, circle.y, 0,
          circle.x, circle.y, currentRadius
        );
        gradient.addColorStop(0, circle.color);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

        ctx.beginPath();
        ctx.arc(circle.x, circle.y, currentRadius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDarkMode, isAnimationDisabled]);

  return (
    <Box className="animated-background-container">
      {!isAnimationDisabled && (
        <canvas ref={canvasRef} className="animated-background-canvas" />
      )}
      <Box className="animated-background-content">
        {children}
      </Box>
    </Box>
  );
};

export default AnimatedBackground;
